import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { LineChart, Line, ResponsiveContainer, Tooltip } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cache = new Map();

export const useLegislatorHistory = (legislatorId) => {
  const [history, setHistory] = useState(cache.get(legislatorId) || null);
  const timer = useRef(null);
  useEffect(() => {
    let cancelled = false;
    if (cache.has(legislatorId)) { setHistory(cache.get(legislatorId)); return undefined; }
    setHistory(null);
    const poll = async (attempt) => {
      try {
        const r = await axios.get(`${API}/legislators/${legislatorId}/history`, { validateStatus: (s) => s === 200 || s === 202 });
        if (cancelled) return;
        if (r.status === 202) { if (attempt < 20) timer.current = setTimeout(() => poll(attempt + 1), 5000); return; }
        cache.set(legislatorId, r.data);
        setHistory(r.data);
      } catch { /* leave null: sparklines simply don't render */ }
    };
    poll(0);
    return () => { cancelled = true; clearTimeout(timer.current); };
  }, [legislatorId]);
  return history;
};

export const Sparkline = ({ history, field, format = (v) => v, color = "#f97316", testId }) => {
  const data = (history?.congresses || []).filter((c) => c[field] != null).map((c) => ({ congress: c.congress, v: c[field] }));
  if (data.length < 2) return null;
  return (
    <div className="h-8 mt-1 -mb-1" data-testid={testId} title={`Per-Congress trend, ${data[0].congress}th to ${data[data.length - 1].congress}th`}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
          <Tooltip cursor={false} contentStyle={{ fontSize: 11, padding: "2px 6px" }} labelFormatter={(l) => `${l}th Congress`} formatter={(v) => [format(v), ""]} separator="" />
          <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} dot={{ r: 1.5 }} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export const Callout = ({ history, metric }) => {
  const c = (history?.callouts || []).find((x) => x.metric === metric);
  if (!c) return null;
  return (
    <p className="text-xs text-orange-600 pl-3 border-l-2 border-orange-400 mt-2" data-testid={`callout-${metric}`}>
      ⚡ {c.text}
    </p>
  );
};

export const AnomalyFlag = ({ history, metric }) => {
  const a = (history?.anomalies || []).find((x) => x.metric === metric);
  if (!a) return null;
  return (
    <span className="ml-1 text-orange-500 cursor-help select-none" title={a.text} data-testid={`anomaly-${metric}`} aria-label={a.text}>⚡</span>
  );
};
